// Admin tools for the legal measures (docs/JURIDISCH.md): policy switches, notices and
// takedowns, blocklist, and the sharing basis of books. Metadata only: no private content.
import { useState } from "react";
import type { AdminBookHit, Book, Notice, Platform, PolicyMode } from "../api";
import { useAuth } from "../auth";
import { Card, ErrorNote, LegalBadges, Spinner, useLoad } from "../ui";

function useAction() {
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  async function run(fn: () => Promise<unknown>, done?: string) {
    setError(null);
    setMsg(null);
    try {
      await fn();
      if (done) setMsg(done);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  }
  return { error, msg, run, setMsg };
}

export function PolicyCard() {
  const { api } = useAuth();
  const platform = useLoad(() => api.platform(), [api]);
  const { error, run } = useAction();
  const [countries, setCountries] = useState<string | null>(null);
  if (platform.error) return <ErrorNote error={platform.error} />;
  if (!platform.data) return <Spinner />;
  const p = platform.data;
  const update = (body: Parameters<typeof api.updatePlatform>[0]) =>
    run(async () => platform.setData(await api.updatePlatform(body)));
  const mode = (key: "provenance_mode" | "public_basis_mode", label: string, help: string) => (
    <label className="row">
      {label}
      <select value={p[key]} onChange={(e) => void update({ [key]: e.target.value as PolicyMode })}>
        <option value="warn">waarschuwen en vastleggen</option>
        <option value="block">blokkeren</option>
      </select>
      <span className="muted small">{help}</span>
    </label>
  );
  return (
    <Card>
      <h2>Juridisch beleid</h2>
      <div className="stack">
        {mode(
          "provenance_mode",
          "Herkomstcontrole",
          "DRM, TDM-voorbehoud, piraterijkenmerken bij upload.",
        )}
        {mode(
          "public_basis_mode",
          "Openbaar zonder grondslag",
          "Boek openbaar maken zonder publiek domein of licentie.",
        )}
        <label className="row">
          Landen
          <select
            value={p.country_mode}
            onChange={(e) => void update({ country_mode: e.target.value as Platform["country_mode"] })}
          >
            <option value="off">uit</option>
            <option value="warn">waarschuwen en vastleggen</option>
            <option value="block">blokkeren</option>
          </select>
          <input
            type="text"
            placeholder="Toegestaan, bv. NL,BE"
            value={countries ?? p.allowed_countries.join(",")}
            onChange={(e) => setCountries(e.target.value)}
            aria-label="Toegestane landen"
          />
          <button
            className="btn btn-ghost small"
            onClick={() =>
              void update({
                allowed_countries: (countries ?? "")
                  .split(",")
                  .map((c) => c.trim().toUpperCase())
                  .filter((c) => /^[A-Z]{2}$/.test(c)),
              }).then(() => setCountries(null))
            }
          >
            Opslaan
          </button>
        </label>
        <p className="muted small">
          Het land is zelf opgegeven door de gebruiker. Blokkeren van een niet-toegestaan land
          geldt alleen als er landen zijn ingevuld.
        </p>
      </div>
      <ErrorNote error={error} />
    </Card>
  );
}

const NOTICE_STATUS: Record<Notice["status"], string> = {
  open: "Open",
  actioned: "Afgehandeld (verwijderd)",
  rejected: "Afgewezen",
};

export function NoticesCard() {
  const { api } = useAuth();
  const [status, setStatus] = useState("open");
  const notices = useLoad(() => api.notices(status || undefined), [api, status]);
  return (
    <Card>
      <h2>Meldingen</h2>
      <label className="row">
        Toon
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">open</option>
          <option value="">alle</option>
          <option value="actioned">afgehandeld</option>
          <option value="rejected">afgewezen</option>
        </select>
      </label>
      <ErrorNote error={notices.error} />
      {!notices.data ? (
        <Spinner />
      ) : notices.data.length === 0 ? (
        <p className="muted">Geen meldingen.</p>
      ) : (
        notices.data.map((n) => <NoticeItem key={n.id} notice={n} onChange={notices.reload} />)
      )}
    </Card>
  );
}

function NoticeItem({ notice: n, onChange }: { notice: Notice; onChange: () => void }) {
  const { api } = useAuth();
  const { error, msg, run } = useAction();
  const [q, setQ] = useState(n.isbn || n.work_title);
  const [hits, setHits] = useState<AdminBookHit[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState(
    `Verwijderd na een melding van de rechthebbende van "${n.work_title}" (referentie ${n.id.slice(0, 8)}).`,
  );
  const [blockFile, setBlockFile] = useState(true);
  const [blockIsbn, setBlockIsbn] = useState(false);
  const [decision, setDecision] = useState("");

  return (
    <div className="admin-notice stack">
      <div>
        <strong>{n.work_title}</strong>
        {n.work_author && <> — {n.work_author}</>} {n.isbn && <span className="muted">ISBN {n.isbn}</span>}
        <br />
        <span className="muted small">
          {new Date(n.created_at).toLocaleString("nl-NL")} · {n.kind} · {n.reporter_role} ·{" "}
          {n.reporter_name} &lt;{n.reporter_email}&gt;
          {n.organisation && ` · ${n.organisation}`} · {NOTICE_STATUS[n.status]}
          {n.books_removed > 0 && ` · ${n.books_removed} boek(en) verwijderd`}
        </span>
      </div>
      <p className="small">{n.explanation}</p>
      {n.location && <p className="small muted">Locatie: {n.location}</p>}
      {n.decision && <p className="small">Besluit: {n.decision}</p>}
      {n.status === "open" && (
        <details>
          <summary className="small">Afhandelen</summary>
          <div className="stack">
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => setHits(await api.findBooks(q)));
            }}
          >
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   aria-label="Zoek boeken (titel, auteur, ISBN, hash)" />
            <button className="btn btn-ghost small" type="submit">
              Zoek geüploade exemplaren
            </button>
          </form>
          {hits && (
            <ul className="findings">
              {hits.length === 0 && <li className="muted">Niets gevonden.</li>}
              {hits.map((b) => (
                <li key={b.id}>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={selected.has(b.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(b.id);
                        else next.delete(b.id);
                        setSelected(next);
                      }}
                    />
                    <span>
                      {b.title} — {b.authors.join(", ")} {b.isbn && `(ISBN ${b.isbn})`}{" "}
                      <span className="muted small">
                        eigenaar {b.owner_email ?? b.owner_id.slice(0, 8)}, {b.created_at.slice(0, 10)}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <label>
            Motivering voor de eigenaar
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="row">
            <label className="check">
              <input type="checkbox" checked={blockFile} onChange={(e) => setBlockFile(e.target.checked)} />
              <span>Zelfde bestand blokkeren</span>
            </label>
            <label className="check">
              <input type="checkbox" checked={blockIsbn} onChange={(e) => setBlockIsbn(e.target.checked)} />
              <span>ISBN blokkeren</span>
            </label>
            <button
              className="btn btn-danger small"
              disabled={selected.size === 0}
              onClick={() => {
                if (!window.confirm(`${selected.size} boek(en) definitief verwijderen?`)) return;
                void run(
                  () =>
                    api.takedown(n.id, {
                      book_ids: [...selected],
                      reason,
                      block_file: blockFile,
                      block_isbn: blockIsbn,
                    }),
                  "Verwijderd.",
                ).then((ok) => ok && onChange());
              }}
            >
              Verwijderen ({selected.size})
            </button>
          </div>
          <div className="row">
            <input type="text" placeholder="Besluit zonder verwijdering (motivering)" value={decision}
                   onChange={(e) => setDecision(e.target.value)} aria-label="Besluit" />
            <button
              className="btn btn-ghost small"
              disabled={decision.trim().length < 3}
              onClick={() =>
                void run(() => api.decideNotice(n.id, "rejected", decision), "Afgewezen.").then(
                  (ok) => ok && onChange(),
                )
              }
            >
              Afwijzen
            </button>
            <button
              className="btn btn-ghost small"
              disabled={decision.trim().length < 3}
              onClick={() =>
                void run(() => api.decideNotice(n.id, "actioned", decision), "Afgehandeld.").then(
                  (ok) => ok && onChange(),
                )
              }
            >
              Afgehandeld (anders)
            </button>
          </div>
          </div>
        </details>
      )}
      {msg && <p className="muted">{msg}</p>}
      <ErrorNote error={error} />
    </div>
  );
}

export function BlocksCard() {
  const { api } = useAuth();
  const blocks = useLoad(() => api.blocks(), [api]);
  const { error, run } = useAction();
  const [kind, setKind] = useState<"isbn" | "sha256">("isbn");
  const [value, setValue] = useState("");
  return (
    <Card>
      <h2>Blokkeerlijst</h2>
      <p className="muted small">
        Bestanden (hash) en ISBN's die niet opnieuw geüpload mogen worden, bijvoorbeeld na een
        gegronde melding of op verzoek van een uitgever.
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          void run(() => api.addBlock({ kind, value })).then((ok) => {
            if (ok) {
              setValue("");
              blocks.reload();
            }
          });
        }}
      >
        <select value={kind} onChange={(e) => setKind(e.target.value as "isbn" | "sha256")}>
          <option value="isbn">ISBN</option>
          <option value="sha256">Bestandshash (sha256)</option>
        </select>
        <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Waarde" />
        <button className="btn btn-ghost small" type="submit" disabled={value.trim().length < 10}>
          Toevoegen
        </button>
      </form>
      <ErrorNote error={error ?? blocks.error} />
      {blocks.data && blocks.data.length > 0 && (
        <ul className="findings">
          {blocks.data.map((b) => (
            <li key={b.id}>
              {b.kind}: <code>{b.value.length > 20 ? `${b.value.slice(0, 20)}…` : b.value}</code>{" "}
              <span className="muted small">{b.reason}</span>{" "}
              <button
                className="link small"
                onClick={() => void run(() => api.removeBlock(b.id)).then(() => blocks.reload())}
              >
                verwijderen
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function SharingCard() {
  const { api } = useAuth();
  const { error, msg, run, setMsg } = useAction();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<AdminBookHit[] | null>(null);
  return (
    <Card>
      <h2>Delen en publiek domein</h2>
      <p className="muted small">
        Een boek openbaar maken mag alleen met een grondslag (publiek domein of licentie). Zonder
        grondslag volgt een waarschuwing of weigering (zie Juridisch beleid). De
        publiek-domeincheck is een advies op basis van sterfjaren van auteurs (Open Library):
        controleer altijd ook de vertaler en de uitgave.
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => setHits(await api.findBooks(q)));
        }}
      >
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Titel, auteur of ISBN" aria-label="Zoek boek" />
        <button className="btn btn-ghost small" type="submit" disabled={q.trim().length < 2}>
          Zoeken
        </button>
      </form>
      {hits?.map((b) => (
        <div key={b.id} className="row admin-notice">
          <span>
            {b.title} — {b.authors.join(", ")} <LegalBadges book={b} />
          </span>
          <select
            defaultValue=""
            aria-label="Grondslag"
            onChange={(e) => {
              const basis = e.target.value as Book["rights_basis"];
              if (basis)
                void run(async () => {
                  const r = await api.setRights(b.id, { rights_basis: basis });
                  setMsg(`Grondslag: ${r.rights_basis}.`);
                });
            }}
          >
            <option value="">grondslag…</option>
            <option value="private">privé (geen)</option>
            <option value="public_domain">publiek domein</option>
            <option value="licensed">licentie</option>
          </select>
          <button
            className="btn btn-ghost small"
            onClick={() =>
              void run(async () => {
                const r = await api.setRights(b.id, {
                  visibility: b.visibility === "public" ? "private" : "public",
                });
                setMsg(
                  `Zichtbaarheid: ${r.visibility}.` +
                    (r.warnings.length ? ` Waarschuwing: ${r.warnings.join("; ")}` : ""),
                );
                setHits((h) => h?.map((x) => (x.id === b.id ? { ...x, visibility: r.visibility } : x)) ?? null);
              })
            }
          >
            {b.visibility === "public" ? "Privé maken" : "Openbaar maken"}
          </button>
          <button
            className="btn btn-ghost small"
            onClick={() =>
              void run(async () => {
                const r = await api.publicDomainCheck(b.id);
                setMsg(`Publiek domein: ${r.status} — ${r.reasons.join("; ")}`);
              })
            }
          >
            Publiek-domeincheck
          </button>
        </div>
      ))}
      {msg && <p className="note">{msg}</p>}
      <ErrorNote error={error} />
    </Card>
  );
}
